/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { BasicModal } from 'src/core/components/BasicModal';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import {
    logButtonClick,
    logButtonSuccessfulClick,
    logMultiLingualInProjectCreation,
    logProjectClicked,
    logSelectLanguageInProjectCreation
} from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { FileType } from 'src/core/types/FileType';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { SignedOutEmptyCustomization } from 'src/languageStudio/appShell/SignedOutEmptyCustomization';
import { getCreationLanguageResourceSettings } from 'src/modules/qnaMaker/qnaMakerUtils';
import { QnaMakerProjectsTable } from 'src/qnaMaker/appShell/QnaMakerProjectsPage/components/QnaMakerProjectsTable';
import { QnaMakerProjectsTableCommandBar } from 'src/qnaMaker/appShell/QnaMakerProjectsPage/components/QnaMakerProjectsTableCommandBar';
import { QnaMakerProjectStore } from 'src/qnaMaker/core/stores/QnaMakerProjectStore';
import {
    QnaMakerProjectCreationModal,
    QnaMakerProjectDto
} from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';

const { styleDiv } = getStylistV2('ApplicationsPage');
const ProjectsTableWindow = styleDiv('KnowledgeBasesTableWindow', { height: '100%', overflowY: 'auto' });

const defaultModalsState = {
    isCreateProjectModalHidden: true,
    isConnectToAzureSearchModalHidden: true,
    isDeleteProjectModalHidden: true,
    isImportProjectModalHidden: true
};

const messages = defineMessages({
    pageTitle: {
        id: 'qnaMaker.qnaMakerProjectsPage.pageTitle',
        defaultMessage: 'Select the project you want to work with'
    },
    createFailed: {
        id: 'qnaMaker.qnaMakerProjectsPage.createFailed',
        defaultMessage: 'Create project failed'
    },
    exportProjectStart: {
        id: 'qnaMaker.qnaMakerProjectsPage.exportProjectStart',
        defaultMessage: 'Exporting project ...'
    },
    exportProjectSuccess: {
        id: 'qnaMaker.qnaMakerProjectsPage.exportProjectSuccess',
        defaultMessage: 'Export project completed'
    },
    exportProjectFailed: {
        id: 'qnaMaker.qnaMakerProjectsPage.exportProjectFailed',
        defaultMessage: 'Export project failed'
    },
    importProjectStart: {
        id: 'qnaMaker.qnaMakerProjectsPage.importProjectStart',
        defaultMessage: 'Importing project ...'
    },
    importProjectSuccess: {
        id: 'qnaMaker.qnaMakerProjectsPage.importProjectSuccess',
        defaultMessage: 'Import project completed'
    },
    importProjectFailed: {
        id: 'qnaMaker.qnaMakerProjectsPage.importProjectFailed',
        defaultMessage: 'Import project failed'
    },
    deleteProjectTitle: {
        id: 'qnaMaker.qnaMakerProjectsPage.deleteProjectTitle',
        defaultMessage: 'Delete selected project(s)?'
    },
    deleteProjectBody: {
        id: 'qnaMaker.qnaMakerProjectsPage.deleteProjectBody',
        defaultMessage: 'Are you sure you want to delete the selected project(s)?'
    },
    deleteProjectStart: {
        id: 'qnaMaker.qnaMakerProjectsPage.deleteProjectStart',
        defaultMessage: 'Deleting project(s) ...'
    },
    deleteProjectSuccess: {
        id: 'qnaMaker.qnaMakerProjectsPage.deleteProjectSuccess',
        defaultMessage: 'Delete project(s) completed'
    },
    deleteProjectFailed: {
        id: 'qnaMaker.qnaMakerProjectsPage.deleteProjectFailed',
        defaultMessage: 'Delete project(s) failed'
    },
    emptyTablePlaceholder: {
        id: 'qnaMaker.qnaMakerProjectsPage.emptyTablePlaceholder',
        defaultMessage: 'No projects to display.'
    },
    noSubscriptionOrResourceGroupTablePlaceholder: {
        id: 'qnaMaker.qnaMakerProjectsPage.noSubscriptionOrResourceGroupTablePlaceholder',
        defaultMessage: 'Please select a subscription and an authoring resource first.'
    },
    noResourceSelected: {
        id: 'qnaMaker.qnaMakerProjectsPage.noResourceSelected',
        defaultMessage: 'Select azure language resource to get started'
    },
    emptyStateMsg: {
        id: 'qnaMaker.qnaMakerProjectsPage.emptyStateMsg',
        defaultMessage: 'Create a new project to get started'
    },
    unconnectedStateTitle: {
        id: 'qnaMaker.qnaMakerProjectsPage.unconnectedStateTitle',
        defaultMessage: 'Start by connecting Azure search to the language resource'
    },
    unconnectedStateBody: {
        id: 'qnaMaker.qnaMakerProjectsPage.unconnectedStateBody',
        defaultMessage:
            'To create a custom question answering project and add data sources to it, you would need to link an Azure search resource to the language resource.'
    },
    refreshPageMessage: {
        id: 'qnaMaker.qnaMakerProjectsPage.refreshPageMessage',
        defaultMessage: 'After connecting your resource to Azure Search, please refresh the page to continue with project creation.'
    },
    connectToAzureSearch: {
        id: 'qnaMaker.qnaMakerProjectsPage.connectToAzureSearch',
        defaultMessage: 'Connect to Azure search.'
    },
    connectToAzureSearchModalBody: {
        id: 'qnaMaker.qnaMakerProjectsPage.unconnectedStateLearnMore',
        defaultMessage:
            'You don’t have an Azure Search resource linked to your language resource. To create a custom question answering project and add data sources to it, you will need to link an Azure Search resource.'
    },
    invalidProjName: {
        id: 'qnaMaker.qnaMakerProjectsPage.invalidProjName',
        defaultMessage: 'Project name should include only alphanumeric characters and hyphens.'
    }
});

export const qnaProjectNameBlacklistValidator = FormValidators.createBlacklistValidator({
    errorMessage: messages.invalidProjName,
    characterList: `\\\\/\:\?\&\=\*\+\(\)\%\$\~\!\>\<\%\@\#\_\\\,\'\.\^\\[\\]\ `
});

export const QnaMakerProjectsPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { dispatcher, rootStore } = useGlobalState();
    const { selection, selectedItems } = useSelection<QnaMakerProjectStore>();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const { isAccessibleMode } = useZoomLevel();

    const [importFileType, setImportFileType] = React.useState<Extract<FileType, 'excel' | 'tsv'>>(null);
    const [query, setQuery] = React.useState('');
    const [sortKey, setSortKey] = React.useState<SortKeyType<QnaMakerProjectStore>>({ key: 'modifiedDate', order: 'desc' });

    const { isSignedIn } = rootStore.authStore;
    const azureSession = rootStore.azureStore.session;

    const isResourceConnectedToAzureSearch =
        Boolean(azureSession.subscriptionId) &&
        Boolean(azureSession.csAccountStore?.path) &&
        azureSession.csAccountStore?.kind === 'TextAnalytics' &&
        Boolean(azureSession.csAccountStore?.ownedSearchResourceId);

    const filteredProjects = orderBy(
        substringSearchStrategy(rootStore.qnaProjectStoreHouse.items, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    React.useEffect(() => {
        if (history.location.state && (history.location.state as boolean)) {
            setModalsState(
                isResourceConnectedToAzureSearch ? { isCreateProjectModalHidden: false } : { isConnectToAzureSearchModalHidden: false }
            );
        }
    }, []);

    React.useEffect(() => {
        if (rootStore.serviceKey.value && azureSession.csAccountStore?.kind === 'TextAnalytics') {
            dispatcher.dispatch('getQnaMakerProjects', { context: { forceRefresh: true }, tracker: projectsProgressTracker });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id, azureSession.csAccountStore?.kind]);

    const projectsProgressTracker = useProgressTracker();
    const defaultProgressTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
            setImportFileType(null);
        }
    });

    const openProject = (projectId: string): void => {
        logProjectClicked(serviceName, 'ProjectList', projectId);
        history.push(routeTable.qnaMaker.projectPage(projectId));
    };

    const getCultureName = (code: string) => LuisConstants.QnaMaker.SupportedProjectLangues.find(item => item.code === code)?.name ?? '';

    const onProjectCreated = (dto: QnaMakerProjectDto) => {
        logSelectLanguageInProjectCreation(serviceName, getCultureName(dto.language), dto.name);

        if (dto.multilingualResource) {
            logMultiLingualInProjectCreation(serviceName, dto.name);
        }

        setModalsState({ isCreateProjectModalHidden: true });
        openProject(dto.name);
    };

    const exportProject = (fileType: Extract<FileType, 'excel' | 'tsv'>): void => {
        dispatcher.dispatch('exportQnaMakerProject', {
            fileType,
            projectName: selectedItems[0].name,
            toastData: {
                running: { title: localize(messages.exportProjectStart) },
                completed: { title: localize(messages.exportProjectSuccess) },
                failed: { title: localize(messages.exportProjectFailed) }
            },
            context: { appId: selectedItems[0].id }
        });
        logButtonClick(serviceName, 'ProjectList', 'Export', null, selectedItems[0].name);
        selection.setAllSelected(false);
    };

    const importProject = ({ file, name }: { file: File; name: string }) => {
        dispatcher.dispatch('importQnaMakerProject', {
            file,
            fileType: importFileType,
            projectName: name || file.name.split('.')[0],
            tracker: defaultProgressTracker,
            toastData: {
                running: { title: localize(messages.importProjectStart) },
                completed: { title: localize(messages.importProjectSuccess) },
                failed: { title: localize(messages.importProjectFailed) }
            }
        });
        logButtonSuccessfulClick(serviceName, 'ProjectList', 'Import', null, name || file.name.split('.')[0]);
    };

    const deleteProjects = (): void => {
        dispatcher.dispatch('deleteQnaMakerProjects', {
            projectIds: selectedItems.map(selectedItem => selectedItem.id),
            toastData: {
                running: { title: localize(messages.deleteProjectStart) },
                completed: { title: localize(messages.deleteProjectSuccess) },
                failed: { title: localize(messages.deleteProjectFailed) }
            },
            tracker: defaultProgressTracker
        });

        selectedItems.forEach(item => logButtonSuccessfulClick(serviceName, 'ProjectList', 'Delete', null, item.name));
    };

    const getTablePlaceholder = (): string => {
        const { subscriptionId, csAccountStore } = rootStore.azureStore.session;
        if (!subscriptionId || !csAccountStore) {
            return localize(messages.noSubscriptionOrResourceGroupTablePlaceholder);
        }

        return localize(messages.emptyTablePlaceholder);
    };

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof QnaMakerProjectStore, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const azureSearchFeatureToggleModalProps = () => ({ onSubmit: () => history.push(routeTable.qnaMaker.azureSearchPage()) });

    const azureSearchFeatureToggleLinkProps = () => ({ onClick: () => history.push(routeTable.qnaMaker.azureSearchPage()) });

    const NoAzureSearchStateMessage = (
        <Stack horizontalAlign="center">
            <LuisText variant="xLarge" fontWeight={600}>
                {localize(messages.unconnectedStateTitle)}
            </LuisText>
            <br />
            <LuisText styles={{ root: { maxWidth: '650px', textAlign: 'center' } }}>
                {localize(messages.unconnectedStateBody)} &nbsp;
                <Link as="a" {...azureSearchFeatureToggleLinkProps()}>
                    {localize(messages.connectToAzureSearch)}
                </Link>
                <br />
                {localize(messages.refreshPageMessage)} &nbsp;
            </LuisText>
        </Stack>
    );

    const getEmptyState = () => {
        if (!azureSession.csAccountStore) {
            return <EmptyStateView message={<LuisText>{localize(messages.noResourceSelected)}</LuisText>} />;
        }
        if (isResourceConnectedToAzureSearch) {
            return <EmptyStateView message={<LuisText>{localize(messages.emptyStateMsg)}</LuisText>} />;
        }

        return <EmptyStateView message={NoAzureSearchStateMessage} />;
    };
    const commandBar = (
        <QnaMakerProjectsTableCommandBar
            query={query}
            onQueryChange={setQuery}
            selectedItems={selectedItems}
            onExportProject={exportProject}
            projectsCount={filteredProjects.length}
            isCreateProjectModalHidden={modalsState.isCreateProjectModalHidden}
            onDeleteProject={() => {
                setModalsState({ isDeleteProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Delete');
            }}
            onImportProject={fileType => {
                setModalsState({ isImportProjectModalHidden: false });
                setImportFileType(fileType);
                logButtonClick(serviceName, 'ProjectList', 'Import');
            }}
            onCreateProject={() => {
                setModalsState(
                    isResourceConnectedToAzureSearch ? { isCreateProjectModalHidden: false } : { isConnectToAzureSearchModalHidden: false }
                );
                logButtonClick(serviceName, 'ProjectList', 'CreateProject');
            }}
        />
    );

    if (!isSignedIn) {
        return (
            <Page size="container-fluid" pageName="QnaMakerProjectsPage" title={localize(messages.pageTitle)}>
                <LuisText as="h1" variant="xLargePlus" fontWeight={600} data-automation-id="qnaProjects">
                    {localize(messages.pageTitle)}
                </LuisText>

                {commandBar}

                <SignedOutEmptyCustomization service="qnaMaker" serviceDocumentationLink="https://aka.ms/language/answerquestions" />
            </Page>
        );
    }

    return (
        <Page size="container-fluid" pageName="QnaMakerProjectsPage" title={localize(messages.pageTitle)}>
            <Stack tokens={{ childrenGap: 25 }} verticalFill>
                <LuisText as="h1" variant="xLargePlus" fontWeight={600} data-automation-id="qnaProjects">
                    {localize(messages.pageTitle)}
                </LuisText>

                <Stack verticalFill>
                    {commandBar}
                    {projectsProgressTracker.isRunning || rootStore.qnaProjectStoreHouse.items.length ? (
                        <ProjectsTableWindow data-is-scrollable={!isAccessibleMode}>
                            <QnaMakerProjectsTable
                                sortKey={sortKey}
                                selection={selection}
                                items={filteredProjects}
                                isLoading={projectsProgressTracker.isRunning}
                                areItemsFiltered={Boolean(query)}
                                overallItemsCount={filteredProjects.length}
                                emptyPlaceholder={getTablePlaceholder()}
                                onSortColumn={onSortColumn}
                                onProjectClick={openProject}
                            />
                        </ProjectsTableWindow>
                    ) : (
                        getEmptyState()
                    )}

                    <DeletePromptModal
                        onDelete={deleteProjects}
                        disabled={defaultProgressTracker.isRunning}
                        hidden={modalsState.isDeleteProjectModalHidden}
                        dialogContentProps={{ title: localize(messages.deleteProjectTitle) }}
                        onDismiss={() => setModalsState({ isDeleteProjectModalHidden: true })}>
                        <LuisText>{localize(messages.deleteProjectBody)}</LuisText>
                    </DeletePromptModal>

                    <QnaMakerProjectCreationModal
                        onSubmit={onProjectCreated}
                        hidden={modalsState.isCreateProjectModalHidden}
                        languageSettings={getCreationLanguageResourceSettings(rootStore)}
                        onDismiss={() => setModalsState({ isCreateProjectModalHidden: true })}
                    />
                    <ImportModal
                        isNameRequired
                        fileType="zip"
                        onSubmit={importProject}
                        fileName={selectedItems[0]?.name || ''}
                        disabled={defaultProgressTracker.isRunning}
                        hidden={modalsState.isImportProjectModalHidden}
                        nameValidators={[qnaProjectNameBlacklistValidator]}
                        onDismiss={() => setModalsState({ isImportProjectModalHidden: true })}
                    />

                    <BasicModal
                        valid
                        hidden={modalsState.isConnectToAzureSearchModalHidden}
                        onDismiss={() => setModalsState({ isConnectToAzureSearchModalHidden: true })}
                        dialogContentProps={{
                            title: localize(messages.connectToAzureSearch),
                            subText: localize(messages.connectToAzureSearchModalBody)
                        }}
                        doneButtonText={localize(messages.connectToAzureSearch)}
                        {...azureSearchFeatureToggleModalProps()}
                    />
                </Stack>
            </Stack>
        </Page>
    );
});
